import React, { useCallback, useMemo } from 'react';
import { Building2, Calendar, Package, ShieldCheck } from 'lucide-react';
import logger from '../../lib/logger';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import { loggedGet, apiClient } from '../VendorCompetitiveAnalysis/loggedFetch';

/*
 * VendorsDrilldown — thin wrapper adapting the drilldown shape used by
 * the Vendor Competitive Analysis panels into the
 * SharedTransactionsDrilldownModal prop surface. Local replica of the
 * (unexported) VcaDrilldown wrapper in
 * VendorCompetitiveAnalysis/VendorCompetitiveAnalysis.js so /vendors can
 * feed every panel into the ONE shared transactions drilldown without
 * touching that file.
 */
function VendorsDrilldown({ drilldown, vendor, drilldownContext, strict, includeLowConfidence, productFamily, onClose }) {
  const vendorName = drilldown.value || drilldown.nodeValue || vendor;

  const fmtDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—');

  const path = useMemo(() => {
    const qs = new URLSearchParams();
    if (strict === false) qs.set('strict', 'false');
    if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
    if (productFamily) qs.set('product_family', productFamily);
    if (drilldown.department) qs.set('department', drilldown.department);
    if (drilldown.component) qs.set('component', drilldown.component);
    if (drilldown.supplier) qs.set('supplier', drilldown.supplier);
    if (drilldown.securityCategory) qs.set('securityCategory', drilldown.securityCategory);
    if (drilldown.startDate) qs.set('startDate', drilldown.startDate);
    if (drilldown.endDate) qs.set('endDate', drilldown.endDate);
    qs.set('limit', '200');
    return `/api/vendor-analysis/${encodeURIComponent(vendorName || '')}/transactions?${qs}`;
  }, [vendorName, drilldown.department, drilldown.component, drilldown.supplier, drilldown.securityCategory, drilldown.startDate, drilldown.endDate, strict, includeLowConfidence, productFamily]);

  const chips = [
    drilldown.department && { icon: <Building2 size={12} />, label: drilldown.department },
    drilldown.component && { icon: <Package size={12} />, label: drilldown.component },
    drilldown.supplier && { icon: <Building2 size={12} />, label: drilldown.supplier },
    drilldown.securityCategory && { icon: <ShieldCheck size={12} />, label: drilldown.securityCategory, variant: 'filter' },
    (drilldown.startDate || drilldown.endDate) && {
      icon: <Calendar size={12} />,
      label: `${fmtDate(drilldown.startDate)} – ${fmtDate(drilldown.endDate)}`,
    },
    strict !== false && {
      icon: <ShieldCheck size={12} />,
      label: 'Strict vendor evidence',
      variant: 'filter',
    },
  ].filter(Boolean);

  const handleFlag = useCallback(async (row, currentlyFlagged) => {
    logger.vendorAnalysis.info('Drilldown:flag', {
      id: row.id, was_flagged: currentlyFlagged, vendor: vendorName,
    });
    // Use the classification_flags pipeline so flagged rows feed
    // classificationActiveLearning.getCandidates() and bubble to the
    // top of the AI re-review queue (flag_pressure weight = 8.0).
    if (currentlyFlagged) {
      await apiClient.post(`/api/classification/flags/item/${row.id}/resolve`, {
        resolution_type: 'dismissed_not_actionable',
        resolution_notes: `Unflagged from Vendors drilldown (vendor=${vendorName})`,
        status: 'dismissed',
      });
    } else {
      await apiClient.post('/api/classification/flags', {
        item_id: row.id,
        flag_reason: `Flagged from Vendors drilldown — vendor=${vendorName}, component=${row.component_name || 'unknown'}`,
        flag_category: 'other',
        flag_severity: 'warning',
      });
    }
  }, [vendorName]);

  return (
    <SharedTransactionsDrilldownModal
      isOpen={true}
      onClose={onClose}
      title={`${vendorName} transactions`}
      contextLabel={drilldownContext}
      chips={chips}
      queryKey={[
        'vendor-analysis', 'transactions', vendorName,
        drilldown.department, drilldown.startDate, drilldown.endDate,
        strict, includeLowConfidence, productFamily,
      ]}
      queryFn={loggedGet('VendorsTransactionsModal:fetch', path)}
      onFlagForReview={handleFlag}
      vendor={vendorName}
      departmentId={drilldown.departmentId}
      component={drilldown.component}
      productFamily={productFamily}
      startDate={drilldown.startDate}
      endDate={drilldown.endDate}
      defaultConfidenceMode={strict ? 'confirmed' : (includeLowConfidence ? 'all' : 'probable')}
      copyContext={{
        vendor: vendorName,
        department: drilldown.department || null,
        period: drilldownContext || null,
        filters: `confidence ≥ 75%, strict vendor evidence ${strict ? 'ON' : 'OFF'}`,
      }}
      footerNote="Totals reconcile with the page chart. Click a PO to open the full document; flag any misclassified rows for AI review."
    />
  );
}

export default VendorsDrilldown;
